import { Typography } from 'antd'
import { useQuantitativeDataSummaryStats } from 'features/dataLayers'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useRolodexEntries } from 'features/rolodex'
import React, { FC, ReactNode, useMemo } from 'react'
import { RolodexEntryType } from 'types'

import { IndicationThicknessHistogram } from './indicationOverview/IndicationThicknessHistogram'
import { DAMAGE_MECHANISM_MAPPING } from './IndicationOverviewCard'

export interface RioIndicationDetail {
  measurement_unit: string
  damage_mechanism: number
  side?: string
  pit_depth?: number
  description: string
  source: string
  thickness?: number
}

const INDICATION_OVERVIEW_ORDERING_TUPLE_LIST: [
  string,
  (detail?: RioIndicationDetail) => ReactNode,
][] = [
  ['Description', (detail) => detail?.description ?? null],
  [
    'Damage Mechanism',
    (detail) => {
      if (
        detail?.damage_mechanism &&
        detail?.damage_mechanism in DAMAGE_MECHANISM_MAPPING
      ) {
        return DAMAGE_MECHANISM_MAPPING[detail.damage_mechanism]
      }
      return 'Unknown'
    },
  ],
  ['Thickness', (detail) => detail?.thickness ?? null],
  ['Pit Depth', (detail) => detail?.pit_depth ?? null],
  [
    'Side',
    (detail) =>
      detail?.source === MFL_SOURCE
        ? detail?.side
          ? (SIDE_MAPPING[detail.side] ?? null)
          : null
        : null,
  ],
]

const RIO_INDICATION_DETAILS_TYPE = RolodexEntryType.RIO_INDICATION_OVERVIEW
const MFL_SOURCE = 'MFL'
const SIDE_MAPPING: { [key: string]: string } = {
  BS: 'Bottom Side',
  TS: 'Top Side',
}
const { Text } = Typography

interface OverviewConfig {
  show_ref_val_on_histogram: boolean
}

export const Title = () => 'Indication Overview'
export const Width = '24rem'
export const CardView: FC<IndicationDetailCardProps<OverviewConfig>> = ({
  config,
}) => {
  const { indication: item, relevantThicknessCDF } = useIndicationData()
  const { entries: indicationDetails } = useRolodexEntries(
    useMemo(
      () =>
        item
          ? [
              {
                type: RIO_INDICATION_DETAILS_TYPE,
                tags: {
                  indication: item.id,
                },
                limit: 1,
              },
            ]
          : [],
      [item]
    )
  ) as unknown as { entries: { data: RioIndicationDetail }[] }

  const cdfSummaryStats = useQuantitativeDataSummaryStats(relevantThicknessCDF)
  const cdfMax = cdfSummaryStats?.data?.value_range.max
  const cdfMin = cdfSummaryStats?.data?.value_range.min
  return (
    <div>
      <Text strong style={{ marginBottom: '2rem' }}>
        Thickness
      </Text>
      <IndicationThicknessHistogram
        area={relevantThicknessCDF?.data.geometry}
        refValue={
          config?.show_ref_val_on_histogram &&
          item?.classificationInfo?.refVal &&
          cdfMin &&
          cdfMax &&
          item?.classificationInfo?.refVal >= cdfMin &&
          item?.classificationInfo?.refVal <= cdfMax
            ? item?.classificationInfo?.refVal
            : undefined
        }
      />
      <hr />
      {indicationDetails[0]?.data ? (
        <div>
          {INDICATION_OVERVIEW_ORDERING_TUPLE_LIST.map(([label, render]) => {
            const value = render(indicationDetails[0].data)
            // Skip rendering if the value is null
            if (value === null) return null
            return (
              <div
                style={{ display: 'flex', width: '100%', flexDirection: 'row' }}
                key={label}>
                <Text strong style={{ minWidth: '44%' }}>
                  {label}
                </Text>

                <Text>{render(indicationDetails[0].data)}</Text>
              </div>
            )
          })}
        </div>
      ) : (
        <div>Detail information is currently not available</div>
      )}
    </div>
  )
}
